import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, publicAssetUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { getLocalLocation } from "@/lib/location";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { formatDistance } from "@/lib/distance";
import { ArrowLeft, MapPin, TicketPercent, Trophy, SearchX } from "lucide-react";
import { CustomerBottomNav } from "@/components/customer/CustomerBottomNav";

const INR = (v) => (typeof v === "number" ? `₹${v.toLocaleString("en-IN")}` : "—");
const initials = (name) => (name || "?").split(/\s+/).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join("");

const SORT_CHIPS = [
    { key: "price_asc", label: "Lowest Price" },
    { key: "discount_desc", label: "Highest Discount" },
];

export default function ComparePricesPage() {
    const [sp, setSp] = useSearchParams();
    const { user } = useAuth();
    const nav = useNavigate();
    const localLoc = getLocalLocation();
    const query = sp.get("q") || "";
    const homeDistrict = sp.get("district_slug") || user?.district_slug || localLoc?.district_slug || "ernakulam";
    const [sort, setSort] = useState(sp.get("sort") || "price_asc");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const lat = user?.location_lat ?? localLoc?.lat ?? null;
    const lng = user?.location_lng ?? localLoc?.lng ?? null;

    useEffect(() => {
        if (!query) { setData({ matched_products: [] }); setLoading(false); return; }
        setLoading(true);
        const params = new URLSearchParams({ q: query, district_slug: homeDistrict, sort, limit: "50" });
        if (lat != null && lng != null) { params.set("lat", String(lat)); params.set("lng", String(lng)); }
        api.get(`/search/products?${params.toString()}`)
            .then((r) => setData(r.data))
            .catch(() => setData({ matched_products: [] }))
            .finally(() => setLoading(false));
    }, [query, homeDistrict, sort, lat, lng]);

    const districtLabel = useMemo(() => (homeDistrict || "").replace(/-/g, " "), [homeDistrict]);

    const setSort2 = (k) => {
        setSort(k);
        const next = new URLSearchParams(sp);
        next.set("sort", k);
        setSp(next, { replace: true });
    };

    return (
        <div className="min-h-screen bg-surface pb-24 lg:pb-6" data-testid="compare-prices-page">
            <header className="sticky top-0 z-30 border-b border-neutral-200 bg-white">
                <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
                    <button onClick={() => nav(-1)} className="rounded-full p-2 hover:bg-neutral-100" data-testid="compare-back">
                        <ArrowLeft size={18} />
                    </button>
                    <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-orange-600">Compare prices</div>
                        <h1 className="truncate text-lg font-extrabold text-neutral-900 sm:text-xl">
                            Best prices for &ldquo;{query}&rdquo; in <span className="capitalize">{districtLabel}</span>
                        </h1>
                    </div>
                </div>
                <div className="mx-auto flex max-w-5xl items-center gap-2 overflow-x-auto px-4 pb-3" data-testid="compare-sort-chips">
                    {SORT_CHIPS.map((s) => (
                        <button
                            key={s.key}
                            onClick={() => setSort2(s.key)}
                            data-testid={`compare-sort-${s.key}`}
                            className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold transition ${sort === s.key ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 bg-white text-neutral-700 hover:border-orange-400"}`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            </header>

            <main className="mx-auto max-w-5xl px-4 py-6">
                {loading ? (
                    <div className="space-y-4">
                        {Array.from({ length: 2 }).map((_, i) => (
                            <Skeleton key={i} className="h-64 rounded-2xl" />
                        ))}
                    </div>
                ) : !data?.matched_products?.length ? (
                    <EmptyCompare query={query} district={districtLabel} onExpand={() => nav(`/posts?search=${encodeURIComponent(query)}`)} />
                ) : (
                    <div className="space-y-6">
                        {data.matched_products.map((g) => (
                            <ProductGroup key={g.product} group={g} sort={sort} />
                        ))}
                    </div>
                )}
            </main>
            {user?.role === "CUSTOMER" && (
                <CustomerBottomNav homeHref={user?.district_slug ? `/home?district_slug=${user.district_slug}` : "/home"} />
            )}
        </div>
    );
}

function ProductGroup({ group, sort }) {
    const best = group.offers[0];
    const preview = best?.poster_image_url;
    return (
        <section
            data-testid={`compare-group-${group.product.replace(/\s+/g, "-")}`}
            className="card-soft overflow-hidden !p-0"
        >
            <div className="flex flex-col gap-4 border-b border-[var(--border-soft)] bg-[var(--surface-bg)] p-4 sm:flex-row sm:items-center">
                <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white shadow-inner">
                    {preview ? (
                        <img src={publicAssetUrl(preview)} alt={group.product} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : (
                        <div className="text-2xl font-black text-[var(--brand-orange)]">🛍️</div>
                    )}
                </div>
                <div className="flex-1">
                    <div className="section-title text-lg capitalize">{group.product}</div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-[var(--text-muted)]">
                        <span className="inline-flex items-center gap-1 rounded-full bg-[var(--brand-orange)] px-2 py-0.5 font-bold text-white" data-testid="offer-count-pill">
                            {group.offer_count} offer{group.offer_count === 1 ? "" : "s"}
                        </span>
                        <span>sorted by <b>{sort === "price_asc" ? "lowest price" : "highest discount"}</b></span>
                    </div>
                </div>
            </div>
            <ul className="divide-y divide-[var(--border-soft)]">
                {group.offers.map((o, idx) => (
                    <li key={o.post_id} data-testid={`compare-offer-${o.post_id}`}>
                        <OfferRow offer={o} isBest={idx === 0} />
                    </li>
                ))}
            </ul>
        </section>
    );
}

function OfferRow({ offer, isBest }) {
    const nav = useNavigate();
    const goDetail = () => nav(`/posts/${offer.post_id}`);
    const claim = () => nav(`/posts/${offer.post_id}?claim=1`);
    return (
        <div className={`flex flex-col items-start gap-3 p-4 sm:flex-row sm:items-center ${isBest ? "bg-orange-50/60" : ""}`}>
            {/* Shop logo */}
            <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-[var(--brand-orange)] text-sm font-black text-white shadow">
                {offer.shop_logo_url ? (
                    <img src={publicAssetUrl(offer.shop_logo_url)} alt={offer.business_name} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                    <span>{initials(offer.business_name)}</span>
                )}
            </div>
            {/* Business + best price badge + distance */}
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="truncate font-heading text-sm font-semibold text-[var(--text-primary)]">{offer.business_name}</div>
                    {isBest && (
                        <span className="savings-pill !text-[10px] uppercase" data-testid="best-price-badge">
                            <Trophy size={10} /> Best price
                        </span>
                    )}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-[var(--text-muted)]">
                    <span className="capitalize">{offer.business_district}</span>
                    {offer.distance_km != null && (
                        <span className="inline-flex items-center gap-1">
                            <MapPin size={11} /> {formatDistance(offer.distance_km)}
                        </span>
                    )}
                </div>
            </div>
            {/* Price + discount */}
            <div className="flex items-center gap-3">
                <div className="text-right leading-tight">
                    <div className="text-lg font-extrabold text-neutral-900" data-testid="offer-price">{INR(offer.price_after)}</div>
                    {typeof offer.price_before === "number" && offer.price_before !== offer.price_after && (
                        <div className="text-xs text-neutral-400 line-through">{INR(offer.price_before)}</div>
                    )}
                </div>
                {typeof offer.discount_percent === "number" && (
                    <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-[11px] font-bold text-orange-700" data-testid="offer-discount">
                        {offer.discount_percent}% OFF
                    </span>
                )}
            </div>
            {/* CTAs */}
            <div className="flex items-center gap-2">
                {offer.coupon_enabled && (
                    <Button size="sm" variant="outline" onClick={claim} data-testid={`compare-claim-${offer.post_id}`} className="text-xs">
                        <TicketPercent size={13} className="mr-1" /> Claim
                    </Button>
                )}
                <Button size="sm" onClick={goDetail} className="nest-gradient text-xs text-white" data-testid={`compare-view-${offer.post_id}`}>
                    View Offer →
                </Button>
            </div>
        </div>
    );
}

function EmptyCompare({ query, district, onExpand }) {
    return (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-neutral-300 bg-white p-10 text-center" data-testid="compare-empty">
            <SearchX size={40} className="text-neutral-400" />
            <div className="mt-4 text-lg font-bold text-neutral-900">No offers found for &ldquo;{query}&rdquo;</div>
            <div className="mt-1 text-sm text-neutral-500">in <span className="capitalize">{district}</span>. Try a different word or expand your district.</div>
            <Button onClick={onExpand} variant="outline" className="mt-4" data-testid="compare-empty-expand">
                Search all posts instead
            </Button>
        </div>
    );
}
